import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomButton from "../../components/Custom/CustomButton";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import GenricButton from "../../components/Custom/GenericButton";
import { useNavigate } from "react-router-dom";
import OrderCard from "../../components/Biochar/OrderCard";
import OrderDetailsDialog from "../../components/Biochar/OrderDetailsDialog";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import { sharePaymentLink } from "../../utils/BiocharOrder";

const useOrderData = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const fetchOrders = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/biochar/biochar-order");
      const orders = response.data.data;
      setOrders(orders);
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError("Failed to fetch orders. Please try again.");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  return { orders, loading, error, fetchOrders };
};

const BiocharOrderList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  const [orderType, setOrderType] = useState("FARMER");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const { orders, loading, error, fetchOrders } = useOrderData();

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const handleDownloadInvoice = (orderId) => {
    const orderToDownload = orders.find((order) => order.id === orderId);
    if (orderToDownload?.invoice_link) {
      window.open(orderToDownload.invoice_link, "_blank");
    }
  };

  const filteredOrders = Array.isArray(orders)
    ? orders.filter((order) => order.order_type === orderType)
    : [];

  const handleAddNewOrder = () => {
    navigate("/biochar-order/place-order");
  };

  const tabs = [
    { value: "FARMER", label: "Farmer" },
    { value: "INSTITUTION", label: "Institution" },
  ];

  const handleSharePaymentLink = (orderId) => {
    sharePaymentLink(orderId, showAlert, t);
  };

  return (
    <>
      <CustomAppBar />
      <Container maxWidth="xs">
        <div>
          <Grid
            container
            justify="center"
            direction="column"
            style={{ marginTop: "10px" }}
          >
            <CustomTitle>{t("Order List")}</CustomTitle>

            <Box>
              <CustomButton fullWidth onClick={handleAddNewOrder}>
                {t("Add New Sale Order")}
              </CustomButton>
            </Box>

            <Grid container spacing={2} sx={{ mb: 1, mt: 0.5 }}>
              {tabs.map((tab) => (
                <Grid item xs={6} key={tab.value}>
                  <GenricButton
                    backgroundColor={
                      orderType === tab.value ? "primary" : "#e0e0e0"
                    }
                    fontColor={orderType === tab.value ? "#fff" : "#333"}
                    fontSize="14px"
                    padding="7px 10px"
                    fullWidth
                    onClick={() => setOrderType(tab.value)}
                  >
                    {t(tab.label)}
                  </GenricButton>
                </Grid>
              ))}
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
                <CircularProgress />
              </Box>
            ) : filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onViewDetails={handleViewDetails}
                  customButtons={
                    order.order_status === "PAID"
                      ? [
                          {
                            label: "Download Invoice",
                            onClick: () => handleDownloadInvoice(order.id),
                            color: "secondary",
                            disabled: !order.invoice_link,
                          },
                        ]
                      : order.order_status === "UNPAID"
                        ? [
                            {
                              label:
                                order.payment_mode === "CASH"
                                  ? "Share Cash Payment Link"
                                  : "Share Payment Link",
                              onClick: () => handleSharePaymentLink(order.id),
                              color: "secondary",
                            },
                          ]
                        : []
                  }
                />
              ))
            ) : (
              <Box sx={{ textAlign: "center", marginTop: 3 }}>
                <Typography variant="body1">{t("No orders found")}</Typography>
              </Box>
            )}

            <OrderDetailsDialog
              open={detailsOpen}
              order={selectedOrder}
              onClose={() => setDetailsOpen(false)}
            />
          </Grid>
        </div>
      </Container>
    </>
  );
};

export default BiocharOrderList;
